<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">User Transaction History</h4>
        <div class="text-muted">
          Single-user transaction audit trail for investigation and support
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingUserTransactionHistory"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- FILTERS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-label>Select User</b-form-label>
            <v-select
              v-model="userId"
              :options="customerOptions"
              :reduce="(val) => val.value"
              label="text"
              placeholder="Search by username, email..."
              :filterable="false"
              :loading="loadingCustomers"
              @search="onSearchCustomer"
              @input="onUserChange"
              @open="onOpenCustomer"
              @scroll-bottom="onScrollBottomCustomer"
            >
              <template #list-footer>
                <div
                  v-if="customerHasMore"
                  class="text-center pb-1 text-muted"
                >
                  <b-spinner small />
                </div>
              </template>
            </v-select>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- STATS -->
    <b-card
      no-body
      class="mb-3"
      v-if="userId && userTransactionHistory.length > 0"
    >
      <div>
        <b-row>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Transactions</div>
              <div class="h5 font-weight-bold">{{ metadata.total || 0 }}</div>
            </div>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Net Amount</div>
              <div
                class="h5 font-weight-bold"
                :class="netAmount > 0 ? 'text-success' : 'text-danger'"
              >
                {{ formatCurrencyBuiltInFunc(netAmount) }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body v-if="userId">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="d-inline-block mx-1"
              style="width: 100px"
            />
            <label>entries</label>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredItems"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingUserTransactionHistory"
      >
        <template #cell(accountType)="{ item }">
          <b-badge :variant="getTypeVariant(item.accountType)">
            {{ item.accountType }}
          </b-badge>
        </template>

        <template #cell(amount)="{ item }">
          <span
            class="font-weight-semibold"
            :class="
              item.amount > 0
                ? 'text-success'
                : item.amount < 0
                ? 'text-danger'
                : ''
            "
          >
            {{ item.amount > 0 ? "+" : ""
            }}{{ formatCurrencyBuiltInFunc(Math.abs(item.amount)) }}
          </span>
        </template>

        <template #cell(balanceBefore)="{ item }">
          <span class="small">{{
            formatCurrencyBuiltInFunc(item.balanceBefore)
          }}</span>
        </template>

        <template #cell(transactionTimestamp)="{ item }">
          <div class="small">
            {{ formatDateTime(item.transactionTimestamp) }}
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2 mt-2" v-if="metadata.total > 0">
        <b-row>
          <b-col cols="12" sm="6">
            <span class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }} to
              {{ Math.min(currentPage * perPage, metadata.total) }} of
              {{ metadata.total }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex justify-content-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.total"
              :per-page="perPage"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-card v-else class="text-center text-muted py-4">
      Enter a User ID to view transaction history
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BFormLabel,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapState } from "vuex";
import { format } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import debounce from "lodash/debounce";
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BFormLabel,
    vSelect,
    BPagination,
    BSpinner,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      userId: "",
      selectedOption: null,
      customerOptions: [],
      loadingCustomers: false,
      tableFields: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "accountType", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "balanceBefore", label: "Balance Before" },
        { key: "description", label: "Description" },
        { key: "transactionTimestamp", label: "Timestamp" },
      ],
      metadata: { total: 0 },
    };
  },
  computed: {
    ...mapState("reporting", [
      "userTransactionHistory",
      "loadingUserTransactionHistory",
      "userTransactionHistoryPagination",
    ]),
    filteredItems() {
      return this.userTransactionHistory || [];
    },
    netAmount() {
      return (this.userTransactionHistory || []).reduce(
        (sum, item) => sum + (item.amount || 0),
        0,
      );
    },
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchData();
    },
  },
  methods: {
    ...mapActions("reporting", ["getUserTransactionHistory"]),
    ...mapActions("user/list", { getUserListBrief: "getUserListBrief" }),
    formatCurrencyBuiltInFunc,
    onSearchCustomer: debounce(async function (search) {
      if (!search) {
        this.customerOptions = this.selectedOption ? [this.selectedOption] : [];
        return;
      }
      this.loadingCustomers = true;
      try {
        const data = await this.getUserListBrief({
          search,
          limit: 20,
          page: 1,
        });
        const newOptions = (data || []).map((user) => ({
          text: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          value: user.userId,
        }));
        if (this.selectedOption && !newOptions.find((o) => o.value === this.selectedOption.value)) {
          newOptions.unshift(this.selectedOption);
        }
        this.customerOptions = newOptions;
      } catch (e) {
        this.customerOptions = this.selectedOption ? [this.selectedOption] : [];
      } finally {
        this.loadingCustomers = false;
      }
    }, 300),
    async onUserChange(val) {
      this.selectedOption = this.customerOptions.find((o) => o.value === val) || null;
      if (!this.userId) return;
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      if (!this.userId) return;
      const data = await this.getUserTransactionHistory({
        userId: this.userId,
        page: this.currentPage,
        limit: this.perPage,
      });
      if (data?.pagination) {
        this.metadata = {
          total: data.pagination.total || 0,
        };
      }
    },
    formatDateTime(date) {
      return format(new Date(date), "dd/MM/yyyy HH:mm:ss");
    },

    getTypeVariant(type) {
      const variants = {
        deposit: "success",
        "bet-placed": "info",
        "bet-win": "success",
        "bet-refund": "warning",
        withdrawal: "danger",
        bonus: "primary",
      };
      return variants[type] || "secondary";
    },
    handleExport() {
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
